import { useEffect } from 'react';

// Custom hook to highlight and scroll to search terms
const useSearchHighlight = () => {
  useEffect(() => {
    // Function to run highlight logic
    const runHighlight = () => {
      const searchTerm = sessionStorage.getItem('searchHighlight');
      
      if (!searchTerm) return;

      // Clear the stored search term immediately to prevent re-triggering
      sessionStorage.removeItem('searchHighlight');

      // Wait for page to fully render
      setTimeout(() => {
        highlightAndScroll(searchTerm);
      }, 300);
    };

    // Run on mount
    runHighlight();
    
    // Also run when window loads (for full page reloads)
    window.addEventListener('load', runHighlight);
    
    return () => {
      window.removeEventListener('load', runHighlight);
    };
  }, []); // Run only once on mount
};

const highlightAndScroll = (searchTerm) => {
  if (!searchTerm || searchTerm.length < 2) return;

  // Remove any existing highlights
  const existingHighlights = document.querySelectorAll('.search-highlight');
  existingHighlights.forEach(el => {
    const parent = el.parentNode;
    parent.replaceChild(document.createTextNode(el.textContent), el);
    parent.normalize();
  });

  // Get all text nodes in the main content area
  const mainContent = document.querySelector('main') || document.body;
  const walker = document.createTreeWalker(
    mainContent,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        // Skip script, style, and other non-visible elements
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        
        const tagName = parent.tagName.toLowerCase();
        if (['script', 'style', 'noscript', 'iframe', 'input', 'textarea'].includes(tagName)) {
          return NodeFilter.FILTER_REJECT;
        }
        
        // Skip if parent is already a highlight
        if (parent.classList?.contains('search-highlight')) {
          return NodeFilter.FILTER_REJECT;
        }

        // Only accept nodes with actual content matching search term
        if (node.textContent.toLowerCase().includes(searchTerm.toLowerCase())) {
          return NodeFilter.FILTER_ACCEPT;
        }
        
        return NodeFilter.FILTER_REJECT;
      }
    }
  );

  const nodesToHighlight = [];
  let node;
  while ((node = walker.nextNode())) {
    nodesToHighlight.push(node);
  }

  let firstHighlight = null;

  // Highlight all occurrences
  nodesToHighlight.forEach((textNode) => {
    const text = textNode.textContent;
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    
    if (!regex.test(text)) return;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match;
    
    // Reset regex
    regex.lastIndex = 0;
    
    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }
      
      // Create highlighted span
      const highlightSpan = document.createElement('span');
      highlightSpan.className = 'search-highlight';
      highlightSpan.style.cssText = 'background-color: #e21b1b; color: white; padding: 2px 4px; border-radius: 3px; animation: highlightPulse 2s ease-in-out;';
      highlightSpan.textContent = match[0];
      fragment.appendChild(highlightSpan);
      
      if (!firstHighlight) {
        firstHighlight = highlightSpan;
      }
      
      lastIndex = regex.lastIndex;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
    
    // Replace the text node with highlighted content
    if (fragment.childNodes.length > 0) {
      textNode.parentNode.replaceChild(fragment, textNode);
    }
  });

  // Scroll to first highlight
  if (firstHighlight) {
    setTimeout(() => {
      firstHighlight.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }, 100);

    // Remove highlights after 5 seconds
    setTimeout(() => {
      const highlights = document.querySelectorAll('.search-highlight');
      highlights.forEach(el => {
        el.style.transition = 'background-color 0.5s ease, color 0.5s ease';
        el.style.backgroundColor = 'transparent';
        el.style.color = 'inherit';
        
        // After fade, remove the span entirely
        setTimeout(() => {
          const parent = el.parentNode;
          if (parent) {
            parent.replaceChild(document.createTextNode(el.textContent), el);
            parent.normalize();
          }
        }, 500);
      });
    }, 5000);
  }
};

export default useSearchHighlight;
